import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/services/api';
import { DocumentRenderer } from '@/components/legal/DocumentRenderer';
import type { LegalDocMeta } from '@/types/api';
import { formatNumericDate } from '@/i18n/intl';

// document_id → URL slug for DocumentRenderer (matches Phase 1 mapping).
// DocumentRenderer fetches /legal/:doc which resolves document_ids directly,
// so unmapped ids pass through unchanged.
const SLUG: Record<string, string> = {
  tos: 'tos', privacy: 'privacy', ai_notice: 'ai_notice',
  crisis: 'crisis', helper_code: 'helper_code', trfer_aup: 'trfer_aup',
};

export function ReconsentPage() {
  const { user, refresh } = useAuth();
  const { t } = useTranslation('consent');
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [meta, setMeta] = useState<Record<string, LegalDocMeta>>({});
  const docs = user?.legal_consent?.blocked_documents ?? [];

  // Fetch current metadata for every doc once (one call returns all current
  // versions) to render a lightweight "what changed" header per document.
  useEffect(() => {
    let active = true;
    api.legal
      .index()
      .then((res) => {
        if (!active) return;
        const map: Record<string, LegalDocMeta> = {};
        for (const d of res.data ?? []) map[d.document_id] = d;
        setMeta(map);
      })
      .catch(() => {
        /* non-fatal — header simply omits version/date if metadata fails */
      });
    return () => {
      active = false;
    };
  }, []);

  // Not blocked → nothing to re-accept; bounce to the dashboard.
  if (!user?.legal_consent?.blocked) {
    return <Navigate to="/dashboard" replace />;
  }

  const onAccept = async () => {
    setSubmitting(true);
    try {
      const res = await api.consent.accept(docs);
      if (res.status === 'success') {
        await refresh?.();
        navigate('/dashboard', { replace: true });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reconsent-page">
      <h1>{t('reconsent.title')}</h1>
      <p>{t('reconsent.lede')}</p>
      {docs.map((d) => {
        const m = meta[d];
        return (
          <div key={d} className="reconsent-doc">
            {m && (
              <p className="reconsent-doc-summary">
                {/* The title is server data and stays English (#2109 R3-B9). */}
                {m.effective_date ? (
                  <Trans
                    t={t}
                    i18nKey="reconsent.summaryEffective"
                    values={{ title: m.title, version: m.version, date: formatNumericDate(m.effective_date) }}
                    components={{ strong: <strong lang="en" /> }}
                  />
                ) : (
                  <Trans
                    t={t}
                    i18nKey="reconsent.summary"
                    values={{ title: m.title, version: m.version }}
                    components={{ strong: <strong lang="en" /> }}
                  />
                )}
              </p>
            )}
            <DocumentRenderer doc={SLUG[d] ?? d} />
          </div>
        );
      })}
      <button disabled={submitting} onClick={onAccept}>{t('reconsent.accept')}</button>
      <a href="/account/close">{t('reconsent.decline')}</a>
    </div>
  );
}
